import React, { useEffect, useState } from 'react';
import { TextField, TextFieldProps, alpha, useTheme } from '@mui/material';
import { useField } from '@unform/core';
import { styled } from '@mui/material/styles';

type TVTextFieldProps = TextFieldProps & {
  name: string;
};

// Componente estilizado seguindo o mesmo padrão dos outros componentes
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.background.default, 0.8)
      : alpha(theme.palette.common.white, 0.8),
    transition: 'all 0.3s ease',
    
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.light, 0.05),
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
    
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.primary.light, 0.08),
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
  },
}));

export const VTextFieldCep: React.FC<TVTextFieldProps> = ({ name, ...rest }) => {
  const theme = useTheme();
  const { fieldName, registerField, defaultValue, error, clearError } = useField(name);

  const formatValueString = (value: string | undefined | null) => {
    if (value === undefined || value === null || value === '') {
      return '';
    }
    
    return value.replace(/\D/g, '').replace(/(\d{2})(\d{3})(\d{3})/, '$1.$2-$3');
  };

  const [value, setValue] = useState<string>(() => formatValueString(defaultValue));

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => value.replace(/\D/g, ''), // Retorna o valor sem formatação
      setValue: (_, newValue) => setValue(formatValueString(newValue)),
    });
  }, [registerField, fieldName, value]);
  
  const formatInputValue = (inputValue: string) => {
    const formattedValue = inputValue.replace(/\D/g, '');
    const maskedValue = formattedValue.replace(/(\d{2})(\d{3})(\d{3})/, '$1.$2-$3');
    return maskedValue;
  };

  return (
    <StyledTextField
      {...rest}
      error={!!error}
      helperText={error}
      defaultValue={defaultValue}
      value={value || ''}
      onChange={(e) => {
        const inputValue = formatInputValue(e.target.value);
        setValue(inputValue);
        rest.onChange?.({ target: { value: inputValue } } as React.ChangeEvent<HTMLInputElement>);
      }}
      onKeyDown={(e) => {
        if (error) {
          clearError();
        }
        rest.onKeyDown?.(e);
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '12px',
          backgroundColor: theme.palette.mode === 'dark' 
            ? alpha(theme.palette.background.default, 0.8)
            : alpha(theme.palette.common.white, 0.8),
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.light, 0.05),
          },
          '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.primary.light, 0.08),
          },
        },
      }}
    />
  );
};